import { Key, useMemo, useId } from 'react';
import { useDashboardJson } from '../../../hooks';
import { TemplateProvider, useTemplateStore } from './hooks/TemplateProvider';
import { findTemplateByName, logger } from '../../../utils';
import { DashboardJson } from '../../models';
import ErrorBoundaryWrapper from '../ErrorBoundaryWrapper';
import { TemplateViewRenderer } from './hooks/TemplateViewRenderer';

const DEFAULT_PROPS = {};
const DEFAULT_CHILDREN_DEF: unknown[] = [];

type TemplateRendererProps = {
  name: string;
  props?: Record<string, unknown>;
  childrenDef?: unknown[];
};

const useTemplateByName = (name: string) => {
  // check localTemplates
  const templateRegistryJSON = useDashboardJson({ name });
  const [localTemplates] = useTemplateStore(
    store => (store as Record<string, unknown>).localTemplates as ReadonlyArray<DashboardJson>,
  );
  const [importedTemplates] = useTemplateStore(
    store => (store as Record<string, unknown>).importedTemplates as ReadonlyArray<DashboardJson>,
  );
  const json = useMemo(() => {
    const localTemplateJSON = findTemplateByName(localTemplates as ReadonlyArray<DashboardJson>, name);
    if (localTemplateJSON) {
      return localTemplateJSON;
    }
    const importedTemplateJSON = findTemplateByName(importedTemplates as ReadonlyArray<DashboardJson>, name);
    if (importedTemplateJSON) {
      return importedTemplateJSON;
    }
    return templateRegistryJSON;
  }, [localTemplates, importedTemplates, name, templateRegistryJSON]);

  return json;
};

// TODO: Add memoization, but ensure it can re-render upon changes to variables within the template.
export const TemplateRenderer = ({
  name,
  props = DEFAULT_PROPS,
  childrenDef = DEFAULT_CHILDREN_DEF,
}: TemplateRendererProps): JSX.Element => {
  // todo: check and improve the use of the useDashboardJson hook
  // from the DashboardRenderer and TemplateRenderer
  const json = useTemplateByName(name);
  const templateId = useId();

  logger.log('TemplateRenderer', { name, props, childrenDef, json });

  const jsonWithChildren = useMemo(
    () => (json ? { ...json, spec: { ...json.spec, children: childrenDef } } : undefined),
    [json, childrenDef],
  );

  if (!jsonWithChildren) {
    return <div>Dashboard '{name}' not found</div>;
  }
  return (
    <ErrorBoundaryWrapper>
      <TemplateProvider
        key={(props?.key as Key) ?? `i${name}`}
        id={(props?.id as string) ? `${name}_${props.id}` : `${name}${templateId}template`}
        json={jsonWithChildren}
        props={props}
      >
        <TemplateViewRenderer />
        </TemplateProvider>
    </ErrorBoundaryWrapper>
  );
};
